/**
 * EMPIRIC quick-add tiles (cart page, cart drawer, "item added" notification).
 *
 * Defines <empiric-cart-upsell>, the custom element wrapping the row rendered
 * by snippets/empiric-cart-upsell.liquid. Custom elements re-initialize
 * whenever their markup is reattached, which is what happens when Dawn
 * re-renders the cart drawer or the notification sections, so nothing needs
 * manual re-binding.
 *
 * 2026-10-31 (AOV round): rebuilt around the size and color the customer
 * already chose.
 *  - The element carries data-anchor-size / data-anchor-color / data-anchor-variant (read from the
 *    first qualifying tee in the cart by the Liquid). A shared size row sits
 *    above the tiles, preselected to that size. Every tile resolves its own
 *    variant from (color, size): the cart color when the candidate is made in
 *    it, otherwise Black, otherwise the candidate's first color. If that color
 *    is sold out in the chosen size, another color in that size is used.
 *  - The Add button posts the resolved variant id to cart/add.js. On the
 *    cart page the page reloads afterwards so totals, discounts and this row
 *    are recomputed by Liquid. Inside the cart drawer or the notification the
 *    returned sections are rendered back the same way Dawn's product form
 *    does it, and the notification shows both lines (cart tee + added tee).
 *  - The old version added the product's first variant (Apricot / S on
 *    several tees) with no size choice.
 */
(function () {
  if (customElements.get('empiric-cart-upsell')) {
    return;
  }

  function normalize(str) {
    return String(str || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  function colorMatches(a, b) {
    var x = normalize(a);
    var y = normalize(b);
    if (!x || !y) return false;
    return x === y || x.indexOf(y) !== -1 || y.indexOf(x) !== -1;
  }

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
      try {
        return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
      } catch (e) {
        /* fall through */
      }
    }
    return '$' + amount;
  }

  class EmpiricCartUpsell extends HTMLElement {
    connectedCallback() {
      this.mode = this.getAttribute('data-upsell-mode') || 'cart';
      this.anchorSize = (this.getAttribute('data-anchor-size') || '').trim();
      this.anchorColor = (this.getAttribute('data-anchor-color') || '').trim();
      this.size = this.anchorSize;
      this.note = this.querySelector('[data-upsell-note]');
      this.tiles = Array.from(this.querySelectorAll('[data-upsell-tile]')).map(this.readTile.bind(this)).filter(Boolean);

      var pills = Array.from(this.querySelectorAll('[data-upsell-size]'));
      if (pills.length) {
        var pressed = pills.filter(function (p) { return p.getAttribute('aria-pressed') === 'true'; });
        if (!pressed.length) {
          // No size in the cart yet (for example a crop size the tees do not
          // share): nothing is preselected and the button asks for a size.
          this.size = '';
        }
        pills.forEach(function (pill) {
          pill.addEventListener('click', this.onSizePick.bind(this, pill, pills));
        }, this);
      }

      this.addEventListener('click', this.handleClick.bind(this));
      this.renderAll();
    }

    readTile(el) {
      var dataEl = el.querySelector('[data-upsell-variants]');
      if (!dataEl) return null;
      var data;
      try {
        data = JSON.parse(dataEl.textContent);
      } catch (e) {
        return null;
      }
      if (!data || !data.variants || !data.variants.length) return null;
      return {
        el: el,
        data: data,
        button: el.querySelector('[data-empiric-quick-add]'),
        image: el.querySelector('[data-upsell-image]'),
        price: el.querySelector('[data-upsell-price]'),
        color: el.querySelector('[data-upsell-color]'),
        variant: null,
      };
    }

    onSizePick(pill, pills) {
      pills.forEach(function (p) { p.setAttribute('aria-pressed', p === pill ? 'true' : 'false'); });
      this.size = pill.getAttribute('data-upsell-size') || '';
      this.showNote('');
      this.renderAll();
    }

    // The colors a candidate is made in, cart color first, then Black, then
    // the rest in catalog order.
    colorOrder(data) {
      var colors = [];
      data.variants.forEach(function (v) {
        if (v.color && colors.indexOf(v.color) === -1) colors.push(v.color);
      });
      var anchor = this.anchorColor;
      var ordered = [];
      colors.forEach(function (c) { if (anchor && colorMatches(c, anchor)) ordered.push(c); });
      colors.forEach(function (c) { if (ordered.indexOf(c) === -1 && /\bblack\b/i.test(c)) ordered.push(c); });
      colors.forEach(function (c) { if (ordered.indexOf(c) === -1) ordered.push(c); });
      return ordered;
    }

    resolve(data) {
      var size = this.size;
      var needsSize = data.sizeIndex >= 0;
      if (needsSize && !size) return null;
      var colors = this.colorOrder(data);
      if (!colors.length) colors = [''];
      for (var i = 0; i < colors.length; i += 1) {
        var color = colors[i];
        var match = data.variants.filter(function (v) {
          if (color && v.color !== color) return false;
          if (needsSize && v.size !== size) return false;
          return v.available;
        })[0];
        if (match) return match;
      }
      return null;
    }

    renderAll() {
      this.tiles.forEach(this.renderTile.bind(this));
    }

    renderTile(tile) {
      var variant = this.resolve(tile.data);
      tile.variant = variant;
      var needsSize = tile.data.sizeIndex >= 0;
      if (tile.image && variant && variant.image && tile.image.getAttribute('src') !== variant.image) {
        tile.image.setAttribute('src', variant.image);
      }
      if (tile.price && variant) tile.price.textContent = formatMoney(variant.price);
      if (tile.color) {
        var bits = [];
        if (variant && variant.color) bits.push(variant.color);
        if (variant && variant.size) bits.push(variant.size);
        tile.color.textContent = bits.length ? bits.join(' / ') + ' · ' : '';
      }
      if (tile.button && !tile.button.hasAttribute('data-busy')) {
        if (!needsSize || !this.size) {
          tile.button.disabled = needsSize && !this.size;
          tile.button.textContent = needsSize ? 'Pick a size' : 'Add';
        } else if (variant) {
          tile.button.disabled = false;
          tile.button.textContent = 'Add · ' + this.size;
        } else {
          tile.button.disabled = true;
          tile.button.textContent = 'Sold out in ' + this.size;
        }
      }
    }

    showNote(text, isError) {
      if (!this.note) return;
      this.note.textContent = text || '';
      this.note.hidden = !text;
      this.note.classList.toggle('empiric-upsell__note--error', !!isError);
    }

    handleClick(event) {
      var button = event.target.closest('[data-empiric-quick-add]');
      if (!button || button.disabled || button.hasAttribute('data-busy')) return;
      event.preventDefault();
      var tile = this.tiles.filter(function (t) { return t.button === button; })[0];
      if (!tile) return;
      if (!tile.variant) {
        this.showNote('Pick a size first.', true);
        return;
      }
      this.addToCart(tile);
    }

    addToCart(tile) {
      var button = tile.button;
      var label = button.textContent;
      var variantId = tile.variant.id;
      var inDrawer = !!this.closest('cart-drawer');
      var notification = this.closest('cart-notification') ? document.querySelector('cart-notification') : null;
      var cart = inDrawer ? this.closest('cart-drawer') : notification;

      var body = { items: [{ id: variantId, quantity: 1 }] };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map(function (s) { return s.id; }).join(',');
        body.sections_url = window.location.pathname;
      }
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var addUrl = (window.routes && window.routes.cart_add_url) || root + 'cart/add';
      if (addUrl.indexOf('.js') === -1) addUrl += '.js';

      button.setAttribute('data-busy', '1');
      button.disabled = true;
      button.textContent = 'Adding...';
      this.showNote('');

      var self = this;
      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (res) {
          var data = res.data || {};
          var items = data.items || [];
          if (!res.ok || !items.length) {
            button.removeAttribute('data-busy');
            button.textContent = label;
            button.disabled = false;
            self.showNote(data.description || data.message || 'Something went wrong. Open the product page to add it.', true);
            return;
          }
          if (cart && typeof cart.renderContents === 'function' && data.sections) {
            if (notification) {
              // Show the tee from the cart and the one just added as two lines,
              // the way "Add both" does for the Snow Washed set. The cart tee is
              // found by its variant id (the id prefix of the line key): the
              // hash part of a line item key is not stable between renders, so
              // a key read from Liquid does not always match the one in the
              // freshly rendered section.
              var firstVariant = self.getAttribute('data-anchor-variant');
              cart.renderContents({ key: items[0].key, sections: data.sections });
              var holder = document.getElementById('cart-notification-product');
              var html = data.sections['cart-notification-product'];
              if (holder && html && firstVariant) {
                var doc = new DOMParser().parseFromString(html, 'text/html');
                var first = doc.querySelector('[id^="cart-notification-product-' + firstVariant + ':"]');
                var second = doc.querySelector('[id="cart-notification-product-' + items[0].key + '"]');
                if (first && second) {
                  holder.classList.add('cro-set-double');
                  holder.innerHTML = '<div class="cro-set-line">' + first.innerHTML + '</div><div class="cro-set-line">' + second.innerHTML + '</div>';
                }
              }
            } else {
              cart.renderContents(data);
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'empiric-cart-upsell', productVariantId: variantId, cartData: data });
            }
            return;
          }
          window.location.reload();
        })
        .catch(function () {
          button.removeAttribute('data-busy');
          button.textContent = label;
          button.disabled = false;
          self.showNote('Something went wrong. Open the product page to add it.', true);
        });
    }
  }

  customElements.define('empiric-cart-upsell', EmpiricCartUpsell);
})();
